import {
  Box,
  Checkbox,
  CSSReset,
  Flex,
  FormLabel,
  Heading,
  InputGroup,
  InputRightAddon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Switch,
  ThemeProvider,
} from '@chakra-ui/core';
import React, { useState } from 'react';
import ConnectChart from './ConnectChart';
import { fetchWeightedDataSets } from './hooks';

const Weighted: React.FC = () => {
  const [{ dynamicWeight, convictionWeight, fundamentalWeight, additionalWeight }, setWeights] = useState({
    dynamicWeight: 60,
    fundamentalWeight: 40,
    convictionWeight: 0,
    additionalWeight: 0,
  });
  const [additional, setAdditional] = useState<'fundsmith' | 'aphilion'>('fundsmith');
  const [includeAdditional, setIncludeAdditional] = useState(false);

  const handleChangeDynamic = (val: any) =>
    setWeights({
      dynamicWeight: val,
      fundamentalWeight: 100 - val - convictionWeight - additionalWeight,
      convictionWeight,
      additionalWeight,
    });

  const handleChangeFundamental = (val: any) =>
    setWeights({
      dynamicWeight: 100 - val - convictionWeight - additionalWeight,
      fundamentalWeight: val,
      convictionWeight,
      additionalWeight,
    });

  const handleChangeConviction = (val: any) =>
    setWeights({
      dynamicWeight,
      fundamentalWeight: 100 - val - dynamicWeight - additionalWeight,
      convictionWeight: val,
      additionalWeight,
    });

  const handleChangeAdditional = (val: any) =>
    setWeights({
      dynamicWeight,
      fundamentalWeight,
      convictionWeight: 100 - val - dynamicWeight - fundamentalWeight,
      additionalWeight: val,
    });

  const handleChangeIncludeAdditional = (val: boolean) => {
    setIncludeAdditional(val);
    if (val) setWeights({ dynamicWeight: 60, fundamentalWeight: 20, convictionWeight: 0, additionalWeight: 20 });
    else setWeights({ dynamicWeight: 60, fundamentalWeight: 40, convictionWeight: 0, additionalWeight: 0 });
  };

  return (
    <ThemeProvider>
      <CSSReset />
      <Flex maxW="1280px" justify="center">
        <Flex w={includeAdditional ? '4xl' : '2xl'} py={4}>
          <Box>
            <FormLabel whiteSpace="nowrap" p={0}>
              Connect Dynamic
            </FormLabel>
            <InputGroup size="sm" mt={1} w={100}>
              <NumberInput
                min={0}
                max={100 - convictionWeight - additionalWeight}
                value={dynamicWeight}
                onChange={handleChangeDynamic}
                clampValueOnBlur={false}
              >
                <NumberInputField roundedRight={0} />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <InputRightAddon>%</InputRightAddon>
            </InputGroup>
          </Box>

          <Slider
            value={fundamentalWeight}
            onChange={handleChangeFundamental}
            min={0}
            max={100 - convictionWeight - additionalWeight}
            step={1}
            mx="6"
            flexGrow={1}
          >
            <SliderTrack bg="#6D6E71" />
            <SliderFilledTrack bg="#222A35" />
            <SliderThumb />
          </Slider>

          <Box>
            <FormLabel whiteSpace="nowrap" p={0}>
              Connect Fundamental
            </FormLabel>
            <InputGroup size="sm" mt={1} w={100}>
              <NumberInput
                min={0}
                max={100 - convictionWeight - additionalWeight}
                value={fundamentalWeight}
                onChange={handleChangeFundamental}
                clampValueOnBlur={false}
              >
                <NumberInputField roundedRight={0} />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <InputRightAddon>%</InputRightAddon>
            </InputGroup>
          </Box>

          <Slider
            value={convictionWeight}
            onChange={handleChangeConviction}
            min={0}
            max={100 - dynamicWeight - additionalWeight}
            step={1}
            mx="6"
            flexGrow={1}
          >
            <SliderTrack bg="#6D6E71" />
            <SliderFilledTrack bg="#222A35" />
            <SliderThumb />
          </Slider>

          <Box>
            <FormLabel whiteSpace="nowrap" p={0}>
              Connect Conviction
            </FormLabel>
            <InputGroup size="sm" mt={1} w={100}>
              <NumberInput
                min={0}
                max={100 - dynamicWeight - additionalWeight}
                value={convictionWeight}
                onChange={handleChangeConviction}
                clampValueOnBlur={false}
              >
                <NumberInputField roundedRight={0} />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <InputRightAddon>%</InputRightAddon>
            </InputGroup>
          </Box>

          {includeAdditional && (
            <>
              <Slider
                value={additionalWeight}
                onChange={handleChangeAdditional}
                min={0}
                max={100 - dynamicWeight - fundamentalWeight}
                mx="6"
                flexGrow={1}
              >
                <SliderTrack bg="#222A35" />
                <SliderFilledTrack bg="#D0D0D2" />
                <SliderThumb />
              </Slider>

              <Box>
                <FormLabel whiteSpace="nowrap" p={0}>
                  {additional === 'fundsmith' ? 'Fundsmith Equity' : 'Aphilion Q² SICAV'}
                </FormLabel>
                <InputGroup size="sm" mt={1} w={100}>
                  <NumberInput
                    min={0}
                    max={100 - dynamicWeight - fundamentalWeight}
                    value={additionalWeight}
                    onChange={handleChangeAdditional}
                    clampValueOnBlur={false}
                  >
                    <NumberInputField roundedRight={0} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <InputRightAddon>%</InputRightAddon>
                </InputGroup>
              </Box>
            </>
          )}
        </Flex>
      </Flex>
      <Box maxW="1280px" p={4} position="relative">
        <ConnectChart
          startDate={new Date('2021-10-01')}
          useFetchDataSets={fetchWeightedDataSets(
            dynamicWeight,
            fundamentalWeight,
            convictionWeight,
            additional,
            includeAdditional
          )}
        />
      </Box>
      <Box px={4}>
        <Heading size="sm" mb={3}>
          <u>Extra vergelijking</u>
        </Heading>
        Fundsmith
        <Switch
          mx={2}
          isChecked={additional === 'aphilion'}
          onChange={() => setAdditional(additional === 'fundsmith' ? 'aphilion' : 'fundsmith')}
        />
        Aphilion
      </Box>
      <Checkbox px={4} isChecked={includeAdditional} onChange={(e) => handleChangeIncludeAdditional(e.target.checked)}>
        Toevoegen
      </Checkbox>
    </ThemeProvider>
  );
};

export default Weighted;
